import React from "react";
import {useDispatch, useSelector} from "react-redux";
import {getCurrentLeague, getCurrentLeagueError, isLeagueRequested} from "modules/selectors";
import styled from "@emotion/styled";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router";
import {useIsMobile} from "modules/utils";
import {clearCurrenLeagueError, leaveLeague} from "modules/actions/leagues";
import {showPromptModal} from "modules/actions/prompt_modal";
import {MiddleLoading} from "components/MiddleLoading";
import {Button} from "@mui/material";
import {LeaguePrivacy} from "modules";
import {get} from "lodash-es";
import {Exist} from "components";

const Container = styled.section`
	//max-width: 300px;
	padding: 16px 20px 20px;
	gap: 20px;
	background: #292f37;
	border-radius: 8px;
	margin-top: 16px;
	width: 100%;
	flex-shrink: 0;
	align-self: flex-start;
	position: sticky;
	top: 10px;

	button {
		width: 100%;
		max-width: 180px;
	}

	@media (max-width: 880px) {
		max-width: 100%;
		background: transparent;
		button {
			max-width: 100%;
		}
	}
`;

const Title = styled.div`
	font-weight: 700;
	font-size: 18px;
	line-height: 150%;
	margin-bottom: 16px;
`;

const SubTitle = styled.h3`
	font-style: normal;
	font-weight: 700;
	font-size: 16px;
	line-height: 130%;
	color: #ffffff;
	margin-bottom: 10px;
`;

const Description = styled.p`
	font-style: normal;
	font-weight: 400;
	font-size: 16px;
	line-height: 130%;
	display: flex;
	align-items: center;
	color: #ffffff;
	margin-bottom: 15px;

	&.capitalize {
		text-transform: capitalize;
	}
`;

const useAboutGroup = () => {
	const i18n = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const isMobile = useIsMobile();
	const isRequested = useSelector(isLeagueRequested);
	const currentLeague = useSelector(getCurrentLeague);
	const error = useSelector(getCurrentLeagueError);
	const privacyValue = get(currentLeague, "privacy", LeaguePrivacy.PUBLIC);
	const chairman = get(currentLeague, "username", "");
	const clearError = () => {
		dispatch(clearCurrenLeagueError());
	};

	const leaveGroup = () => {
		if (currentLeague) {
			dispatch(
				leaveLeague({
					leagueId: currentLeague.id,
					onSuccess: () => navigate("/groups"),
				})
			);
		}
	};

	const leaveGroupConfirm = () => {
		if (currentLeague) {
			dispatch(
				showPromptModal({
					title: i18n.t("about_league.leave.modal_title", "Are you sure?"),
					description: i18n.t(
						"about_league.leave.modal_description",
						`
					You are leaving the group ${currentLeague.name}. This action cannot be reversed.
				`,
						{
							name: currentLeague.name,
						}
					),
					denyButton: {
						text: i18n.t("about_league.leave.cancel", "No, Cancel"),
					},
					confirmButton: {
						text: i18n.t("about_league.leave.confirm", "Yes, Leave"),
						onClick: leaveGroup,
					},
				})
			);
		}
	};

	return {
		i18n,
		dispatch,
		isRequested,
		currentLeague,
		isMobile,
		error,
		leaveGroupConfirm,
		clearError,
		privacyValue,
		chairman,
	};
};

export const AboutGroup: React.FC = () => {
	const {i18n, isRequested, leaveGroupConfirm, currentLeague, privacyValue, chairman} =
		useAboutGroup();

	if (!currentLeague) {
		return null;
	}

	return (
		<Container>
			<div>
				<Title>{i18n.t("about_league.title", "Settings")}</Title>

				<SubTitle>{i18n.t("about_league.group_type", "Group Type")}</SubTitle>
				<Description className={"capitalize"}>{privacyValue}</Description>

				<Exist when={Boolean(chairman)}>
					<SubTitle>{i18n.t("about_league.chairman", "Chairman")}</SubTitle>
					<Description>{chairman}</Description>
				</Exist>

				<Exist when={Boolean(currentLeague.description)}>
					<SubTitle>{i18n.t("about_league.description", "Description")}</SubTitle>
					<Description className={"capitalize"}>{currentLeague.description}</Description>
				</Exist>

				<SubTitle>{i18n.t("about_league.leave_group", "Leave Group")}</SubTitle>
				<Description>
					{i18n.t(
						"about_league.leave_group_description",
						"If you leave a group, you can always rejoin it should you wish to do so."
					)}
				</Description>

				<Button
					variant={"contained"}
					color={"error"}
					onClick={leaveGroupConfirm}
					aria-haspopup={"dialog"}
				>
					{i18n.t("update_league.leave_button", "Leave Group")}
				</Button>
			</div>

			<MiddleLoading when={isRequested} />
		</Container>
	);
};
